import { ChevronLeft, ChevronRight } from "lucide-react";
import { twMerge } from "tailwind-merge";

interface DotPaginatorProps {
  /** Zero-based current page. */
  page: number;
  pageCount: number;
  onChange: (page: number) => void;
  className?: string;
  label?: string;
}

/**
 * Arrows + expanding-dot pager. Shares the exact design language of the
 * chat's recommendation chips (RecommendationChips): a wide pill for the
 * active page, small dots for the rest. Renders nothing for a single page.
 */
export function DotPaginator({ page, pageCount, onChange, className, label }: DotPaginatorProps) {
  if (pageCount <= 1) return null;

  return (
    <div className={twMerge("flex items-center gap-1.5", className)} aria-label={label}>
      <button
        type="button"
        aria-label="Previous page"
        disabled={page === 0}
        onClick={() => onChange(Math.max(0, page - 1))}
        className="rounded-md p-0.5 text-neutral-400 transition-colors hover:bg-neutral-100 hover:text-neutral-600 disabled:opacity-30"
      >
        <ChevronLeft className="size-3.5" />
      </button>
      {Array.from({ length: pageCount }, (_, i) => (
        <button
          key={i}
          type="button"
          aria-label={`Page ${i + 1}`}
          aria-current={i === page}
          onClick={() => onChange(i)}
          className={twMerge(
            "h-1.5 rounded-full transition-all",
            i === page ? "w-4 bg-primary-500" : "w-1.5 bg-neutral-300"
          )}
        />
      ))}
      <button
        type="button"
        aria-label="Next page"
        disabled={page === pageCount - 1}
        onClick={() => onChange(Math.min(pageCount - 1, page + 1))}
        className="rounded-md p-0.5 text-neutral-400 transition-colors hover:bg-neutral-100 hover:text-neutral-600 disabled:opacity-30"
      >
        <ChevronRight className="size-3.5" />
      </button>
    </div>
  );
}
